import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "https://manavastralu-backend.onrender.com";

/* ── image helpers ──────────────────────────────────────────── */
const getImgUrl = (img) => {
  if (!img) return "/images/s1.png";
  if (img.startsWith("http")) return img;
  if (img.startsWith("uploads/")) return `${API}/${img}`;
  return `${API}/uploads/${img}`;
};

/* Cloudinary: serve exact size + auto format/quality */
// Availability label under the price. "In Stock" / "Out of Stock" always show.
// Set to true to also show "Only N left" when 3 or fewer remain.
const SHOW_LOW_STOCK = false;

const cloudOpt = (url, w) => {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", `/upload/w_${w},f_auto,q_auto:good,dpr_auto/`);
};

/* ── ALL CSS in one string ──────────────────────────────────── */
const STYLES = `
  /* reset */
  *, *::before, *::after { box-sizing: border-box; }
  html, body { overflow-x: hidden; }

  /* shimmer */
  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .sk {
    background: linear-gradient(90deg,#f0e4d4 25%,#faf0e6 50%,#f0e4d4 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 6px;
  }

  /* ── page ── */
  .pd { font-family:'Segoe UI',sans-serif; background:#fdf6ec; min-height:100vh; width:100%; overflow-x:hidden; }
  .pd-wrap { max-width:1100px; margin:0 auto; padding:14px 14px 48px; }

  /* ── breadcrumb ── */
  .pd-bc { display:flex; flex-wrap:wrap; gap:4px; align-items:center; font-size:11px; color:#8a4a30; margin-bottom:14px; }
  .pd-bc a { color:#5c2e1f; cursor:pointer; text-decoration:none; }

  /* ════════════════════════════════════════════════
     MOBILE LAYOUT  (default = single column stack)
     ============================================= */
  .pd-layout {
    display: flex;
    flex-direction: column;   /* ← MOBILE: image on top, info below */
    gap: 20px;
    width: 100%;
  }

  /* ── image section ── */
  .pd-img-col { width: 100%; }

  /* ✅ main image: the FULL picture, never cropped.
     The box takes the photo's own shape (portrait, square or landscape)
     instead of forcing a fixed square / 3:4 frame that cut the top and
     bottom off. max-height keeps a very tall photo from running off-screen. */
  .pd-main-box {
    position: relative;
    width: 100%;
    min-height: 300px;       /* holds the space (and the shimmer) while it loads */
    background: #f5ede0;
    border-radius: 14px;
    overflow: hidden;
  }
  .pd-main-img {
    position: relative;
    display: block;
    width: 100%;
    height: auto;
    max-height: 85vh;
    object-fit: contain;
    margin: 0 auto;
    transition: opacity .28s ease;
  }
  .pd-main-img.vis { animation: fadeUp .28s ease; }

  /* overlay badges */
  .pd-badge {
    position: absolute; z-index: 4;
    font-size: 11px; font-weight: 800;
    padding: 5px 12px; border-radius: 4px;
    letter-spacing: 1px; color: #fff;
  }
  .pd-badge.sold { top:12px; left:12px; background:#c62828; }
  .pd-badge.disc { top:12px; right:12px; background:#5c2e1f; }

  /* wish button */
  .pd-wish {
    position: absolute; bottom:12px; right:12px; z-index:4;
    width:38px; height:38px; border-radius:50%;
    background:#fff; border:1px solid #ecd9bd;
    display:flex; align-items:center; justify-content:center;
    cursor:pointer; font-size:20px;
    box-shadow:0 2px 8px rgba(0,0,0,.1);
  }

  /* counter pill */
  .pd-counter {
    position:absolute; bottom:12px; left:50%; transform:translateX(-50%);
    background:rgba(0,0,0,.42); color:#fff; font-size:11px;
    padding:3px 12px; border-radius:20px; z-index:4; white-space:nowrap;
    backdrop-filter:blur(4px);
  }

  /* ✅ click the picture → full-screen viewer */
  .pd-zoomable { cursor: zoom-in; }
  .pd-zoom-btn {
    position:absolute; bottom:12px; left:12px; z-index:4;
    width:38px; height:38px; border-radius:50%;
    background:#fff; border:1px solid #ecd9bd; color:#5c2e1f;
    display:flex; align-items:center; justify-content:center;
    cursor:pointer; box-shadow:0 2px 8px rgba(0,0,0,.1);
  }
  .lb {
    position:fixed; inset:0; z-index:999990;
    background:rgba(15,8,4,.94);
    display:flex; align-items:center; justify-content:center;
    animation: lbIn .2s ease;
  }
  @keyframes lbIn { from { opacity:0; } to { opacity:1; } }
  .lb-img {
    max-width:96vw; max-height:92vh; width:auto; height:auto;
    object-fit:contain; border-radius:6px; user-select:none;
    -webkit-user-drag:none; cursor:default;
  }
  .lb-close {
    position:absolute; top:14px; right:16px; z-index:2;
    width:44px; height:44px; border-radius:50%; border:none;
    background:rgba(255,255,255,.14); color:#fff; font-size:22px; cursor:pointer;
  }
  .lb-count {
    position:absolute; top:22px; left:50%; transform:translateX(-50%);
    color:#fff; font-size:13px; padding:4px 14px; border-radius:20px;
    background:rgba(255,255,255,.14);
  }
  .lb-nav {
    position:absolute; top:50%; transform:translateY(-50%); z-index:2;
    width:48px; height:48px; border-radius:50%; border:none;
    background:rgba(255,255,255,.14); color:#fff; font-size:30px; line-height:1;
    cursor:pointer; display:flex; align-items:center; justify-content:center;
  }
  .lb-close:hover, .lb-nav:hover { background:rgba(255,255,255,.28); }
  .lb-prev { left:16px; }
  .lb-next { right:16px; }
  @media (max-width:768px) { .lb-nav { display:none; } }  /* phones: swipe instead */

  /* dots */
  .pd-dots { display:flex; justify-content:center; gap:6px; margin-top:10px; }
  .pd-dot {
    width:6px; height:6px; border-radius:50%;
    background:#d4b896; cursor:pointer; border:none; padding:0;
    transition:all .2s;
  }
  .pd-dot.on { background:#5c2e1f; width:18px; border-radius:4px; }

  /* thumb strip */
  .pd-thumbs {
    display:flex; gap:8px; margin-top:10px;
    overflow-x:auto; padding-bottom:4px; scrollbar-width:none;
  }
  .pd-thumbs::-webkit-scrollbar { display:none; }
  .pd-thumb {
    flex-shrink:0; width:60px; height:76px;
    border-radius:6px; overflow:hidden; cursor:pointer;
    border:2px solid transparent; background:#f5ede0;
    transition:border-color .15s;
  }
  .pd-thumb.on  { border-color:#5c2e1f; }
  .pd-thumb img { width:100%; height:100%; object-fit:cover; }

  /* ── info section ── */
  .pd-info-col { width:100%; }

  .pd-name {
    font-family:Georgia,serif; font-size:22px; font-weight:700;
    color:#4a2419; margin:0 0 5px; line-height:1.3; word-break:break-word;
  }
  .pd-meta { font-size:11px; color:#8a4a30; text-transform:uppercase; letter-spacing:.6px; margin:0 0 13px; }
  .pd-divider { width:100%; height:1px; background:#ecd9bd; margin:0 0 16px; }

  /* price */
  .pd-price-row { display:flex; align-items:center; flex-wrap:wrap; gap:10px; margin-bottom:16px; }
  .pd-avail { display:inline-flex; align-items:center; gap:7px; font-size:13px; font-weight:700; margin:-4px 0 14px; }
  .pd-avail.in  { color:#2e7d32; }
  .pd-avail.out { color:#c62828; }
  .pd-avail-dot { width:9px; height:9px; border-radius:50%; background:currentColor; }
  .pd-price { font-family:Georgia,serif; font-size:28px; font-weight:700; color:#4a2419; }
  .pd-orig  { font-size:16px; color:#aaa; text-decoration:line-through; }
  .pd-off   { font-size:12px; font-weight:700; color:#2e7d32; background:#e8f5e9; padding:3px 9px; border-radius:5px; }

  /* selector label */
  .pd-sel-label {
    font-size:11px; font-weight:700; letter-spacing:.8px; color:#8a4a30;
    text-transform:uppercase; display:flex; align-items:center;
    gap:8px; flex-wrap:wrap; margin-bottom:9px;
  }
  .pd-sel-label em  { font-style:normal; color:#5c2e1f; font-weight:600; font-size:12px; text-transform:none; }
  .req-tag {
    font-size:10px; font-weight:700; color:#c62828; background:#fff;
    border:1.5px solid #c62828; padding:2px 10px; border-radius:14px;
    letter-spacing:.4px; display:inline-flex; align-items:center; gap:3px;
  }
  .clear-link {
    font-size:11px; color:#8a4a30; text-decoration:underline;
    cursor:pointer; font-weight:600; letter-spacing:.3px;
  }
  .clear-link:hover { color:#5c2e1f; }

  /* chips */
  .chip-row { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:10px; }
  .chip {
    padding:10px 18px; font-size:13px; font-weight:600;
    border-radius:8px; border:1.5px solid #ddd0be;
    background:#fff; color:#4a2419; cursor:pointer;
    transition:all .15s; min-width:52px; text-align:center;
  }
  .chip:hover:not(.on):not(:disabled) { border-color:#5c2e1f; background:#fdf6ec; }
  .chip.on   { border-color:#5c2e1f; background:#fdf6ec; color:#5c2e1f; border-width:2px; font-weight:700; }
  .chip.err  { border-color:#f0a8a8; }
  .chip:disabled { opacity:.4; cursor:not-allowed; text-decoration:line-through; }

  /* size alert */
  .size-alert {
    padding:9px 12px; background:#fff5f5; border:1px solid #fecaca;
    border-radius:6px; font-size:12px; color:#c62828; font-weight:600;
    margin-bottom:14px;
  }

  /* low stock */
  .low-stock {
    display:flex; align-items:center; gap:6px; font-size:12px;
    color:#c62828; font-weight:700; background:#ffebee;
    padding:9px 12px; border:1px solid #ffcdd2; border-radius:6px; margin-bottom:14px;
  }

  /* ── ACTION ROW: qty + 2 buttons in ONE row
     (✅ restored to original 3-column layout) ── */
  .pd-actions {
    display: grid;
    grid-template-columns: auto 1fr 1fr;
    gap: 8px;
    margin-bottom: 14px;
    width: 100%;
  }
  /* ✅ FIX — grid items default to min-width:auto, which means they
     refuse to shrink below their own content's natural width even
     when sitting inside a 1fr track. That's what let long button
     text ("ADD TO CART", "GO TO CART →") push past the row's edge
     and overflow/"break out" of the container on narrow screens,
     instead of actually fitting inside their column. */
  .pd-actions > * { min-width: 0; }

  /* qty control */
  .qty-ctrl {
    display:flex; align-items:center;
    border:1.5px solid #ecd9bd; border-radius:8px;
    overflow:hidden; height:50px; flex-shrink:0;
  }
  .qty-b {
    width:36px; height:100%; border:none; background:#fff;
    font-size:20px; font-weight:700; color:#5c2e1f;
    cursor:pointer; display:flex; align-items:center; justify-content:center;
    transition:background .15s;
  }
  .qty-b:hover:not(:disabled) { background:#fdf6ec; }
  .qty-b:disabled { opacity:.3; cursor:not-allowed; }
  .qty-n {
    width:38px; height:100%; border-left:1px solid #ecd9bd;
    border-right:1px solid #ecd9bd;
    display:flex; align-items:center; justify-content:center;
    font-size:15px; font-weight:700; color:#4a2419; background:#fff;
  }

  /* CTA buttons */
  .cta {
    height:50px; border:none; border-radius:8px;
    font-size:12px; font-weight:700; letter-spacing:.3px;
    cursor:pointer; transition:all .2s;
    display:flex; align-items:center; justify-content:center;
    white-space:nowrap; gap:4px;
    overflow:hidden; text-overflow:ellipsis; /* ✅ second layer of
      defense — if a button's column is ever squeezed smaller than
      its text (very narrow phones), the text now truncates cleanly
      instead of visually spilling out past the button's edge */
  }
  .cta:hover:not(:disabled) { filter:brightness(1.08); transform:translateY(-1px); }
  .cta:disabled { opacity:.6; cursor:not-allowed; }
  .cta-cart { background:#4a2419; color:#e8d5a3; }
  .cta-cart.added { background:#5c2e1f; }
  .cta-buy  { background:#5c2e1f; color:#fff; }
  .cta-sold { background:#aaa; color:#fff; }
  /* Flipkart-style "Go to cart": outlined, sits where Add to Cart was */
  .cta-go   { background:#fff; color:#4a2419; border:2px solid #4a2419; }
  /* size row flashes red + shakes when the customer tries to add without a size */
  .size-shake { animation: sizeShake .5s ease; }
  .size-shake .chip { border-color:#c62828; }
  .size-shake .size-alert { background:#ffe3e3; border-color:#c62828; }
  @keyframes sizeShake {
    0%,100% { transform:translateX(0); }
    20% { transform:translateX(-8px); } 40% { transform:translateX(8px); }
    60% { transform:translateX(-5px); } 80% { transform:translateX(5px); }
  }

  /* authenticity strip */
  .auth-strip {
    background:#fff; border:1px solid #ecd9bd;
    padding:11px 14px; border-radius:8px;
    display:flex; align-items:center; gap:8px;
    font-size:12px; color:#5c2e1f; margin-bottom:16px;
    flex-wrap:wrap;
  }

  /* delivery badges */
  .del-badges { display:flex; flex-direction:column; gap:7px; margin-bottom:16px; }
  .del-badge  { display:flex; align-items:center; gap:8px; font-size:12px; color:#5c2e1f; }
  .del-badge span:first-child { font-size:15px; }

  /* description */
  .pd-desc-label { font-size:10px; font-weight:700; color:#8a4a30; letter-spacing:.8px; text-transform:uppercase; margin-bottom:7px; }
  .pd-desc { font-size:13.5px; color:#5c2e1f; line-height:1.75; margin:0; word-break:break-word; }

  /* notify box */
  .notify-box { margin-top:18px; background:#fff8f0; border:1px solid #f0c8a0; border-radius:10px; padding:18px 14px; text-align:center; }
  .wa-link {
    display:inline-flex; align-items:center; gap:8px;
    background:linear-gradient(135deg,#25D366,#128c7e);
    color:#fff; padding:11px 22px; border-radius:10px;
    text-decoration:none; font-size:13px; font-weight:700; transition:all .2s;
  }
  .wa-link:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(37,211,102,.4); }

  /* ── related ── */
  .rel-section { margin-top:36px; }
  .rel-header  { text-align:center; margin-bottom:18px; }
  .rel-rule    { display:flex; align-items:center; gap:14px; margin-bottom:4px; }
  .rel-line    { flex:1; height:1px; background:linear-gradient(90deg,transparent,#d4b896,transparent); }
  .rel-title   { font-family:Georgia,serif; font-size:18px; font-weight:700; color:#4a2419; margin:0; white-space:nowrap; }
  .rel-sub     { font-size:10px; color:#8a4a30; letter-spacing:1.5px; text-transform:uppercase; }

  .rel-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .rel-card {
    background:#fff; border-radius:10px; overflow:hidden;
    border:1px solid #ede0cc; cursor:pointer;
    transition:transform .2s, box-shadow .2s;
  }
  .rel-card:hover { transform:translateY(-4px); box-shadow:0 10px 28px rgba(92,46,31,.12); }
  .rel-img-wrap { position:relative; overflow:hidden; aspect-ratio:3/4; background:#f5ede0; }
  .rel-img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .4s; }
  .rel-card:hover .rel-img { transform:scale(1.05); }
  .rel-sold-banner { position:absolute; top:0; left:0; right:0; background:rgba(198,40,40,.88); color:#fff; font-size:9px; font-weight:800; text-align:center; padding:4px 0; letter-spacing:1.5px; }
  .rel-disc-tag { position:absolute; top:8px; left:8px; background:#5c2e1f; color:#fff; font-size:9px; font-weight:700; padding:3px 7px; border-radius:3px; }
  .rel-wish { position:absolute; top:7px; right:7px; width:28px; height:28px; border-radius:50%; background:rgba(255,255,255,.92); border:none; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:14px; box-shadow:0 2px 6px rgba(0,0,0,.12); }
  .rel-body { padding:9px 9px 0; }
  .rel-pname { font-family:Georgia,serif; font-size:12px; color:#4a2419; font-weight:600; margin:0 0 3px; line-height:1.3; overflow:hidden; text-overflow:ellipsis; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
  .rel-pmeta { font-size:9px; color:#8a4a30; text-transform:uppercase; margin-bottom:5px; }
  .rel-pprice { font-family:Georgia,serif; font-size:14px; font-weight:700; color:#4a2419; }
  .rel-porig  { font-size:10px; color:#b08060; text-decoration:line-through; margin-left:4px; }
  .rel-cta { width:100%; padding:9px 0; border:none; font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; cursor:pointer; margin-top:8px; }

  /* ════════════════════════════════════════════════
     DESKTOP overrides (≥ 769px)
     ============================================= */
  @media (min-width: 769px) {
    .pd-wrap  { padding:28px 40px 60px; }

    /* side-by-side layout on desktop */
    .pd-layout { flex-direction: row; gap: 44px; align-items: flex-start; }
    .pd-img-col { width: 460px; flex-shrink: 0; position: sticky; top: 90px; }

    /* desktop: same — the whole picture, at its own shape */
    .pd-main-box { border-radius: 10px; min-height: 360px; }

    /* desktop thumb column beside main image */
    .pd-desk-gallery { display: flex !important; gap: 12px; }
    .pd-mob-gallery  { display: none  !important; }

    .pd-thumb-col { display:flex; flex-direction:column; gap:8px; }
    .pd-thumb     { width:72px; height:90px; }

    .pd-info-col { flex: 1; }
    .pd-name     { font-size:26px; }
    .pd-price    { font-size:30px; }
    .cta         { height:52px; font-size:13px; }
    .qty-ctrl    { height:52px; }
    .qty-b       { width:40px; }
    .qty-n       { width:44px; }

    .rel-grid  { grid-template-columns: repeat(4, 1fr); gap:18px; }
    .rel-title { font-size:22px; }
    .rel-body  { padding:12px 12px 0; }
    .rel-pname { font-size:13px; }
    .rel-pprice{ font-size:15px; }
    .rel-section { margin-top:60px; }
  }
`;

/* ── WhatsApp SVG ──────────────────────────────────────────── */
const WaIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/* ── full-screen picture viewer ────────────────────────────────
   Esc or the ✕ / dark area closes it; ← → keys, the arrow buttons, or a
   swipe move between the product's pictures. */
function Lightbox({ images, index, name, onIndex, onClose }) {
  const touchX = useRef(null);
  const total  = images.length;
  const prev   = () => onIndex((index - 1 + total) % total);
  const next   = () => onIndex((index + 1) % total);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft"  && total > 1) prev();
      else if (e.key === "ArrowRight" && total > 1) next();
    };
    document.addEventListener("keydown", onKey);
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";          // page behind doesn't scroll
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = before;
    };
  }); // re-bound every render so prev/next always use the current picture

  const src = images[index] || images[0];

  return (
    <div className="lb" role="dialog" aria-modal="true" aria-label={`${name} — full picture`}
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null || total < 2) return;
        const d = touchX.current - e.changedTouches[0].clientX;
        touchX.current = null;
        if (Math.abs(d) > 50) (d > 0 ? next() : prev());
      }}>
      <button className="lb-close" aria-label="Close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}>✕</button>

      {total > 1 && <div className="lb-count">{index + 1} / {total}</div>}

      {total > 1 && (
        <button className="lb-nav lb-prev" aria-label="Previous picture"
          onClick={(e) => { e.stopPropagation(); prev(); }}>‹</button>
      )}

      <img className="lb-img" src={cloudOpt(src, 1600)} alt={name}
        onClick={(e) => e.stopPropagation()} />

      {total > 1 && (
        <button className="lb-nav lb-next" aria-label="Next picture"
          onClick={(e) => { e.stopPropagation(); next(); }}>›</button>
      )}
    </div>
  );
}

export default function ProductDetail({ cartItems, setCartItems, wishlist, setWishlist }) {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [product,      setProduct]      = useState(null);
  const [related,      setRelated]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [allImages,    setAllImages]    = useState([]);
  const [activeImg,    setActiveImg]    = useState("");
  const [activeIdx,    setActiveIdx]    = useState(0);
  const [imgReady,     setImgReady]     = useState(false);
  const [thumbsReady,  setThumbsReady]  = useState({});
  const [activeColor,  setActiveColor]  = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [qty,          setQty]          = useState(1);
  const [inCart,       setInCart]       = useState(false);
  const [adding,       setAdding]       = useState(false);
  const [lightbox,     setLightbox]     = useState(false); // full-screen picture viewer
  const [sizeShake,    setSizeShake]    = useState(false); // highlights the size row when a size is missing

  const txRef = useRef(null);
  const sizeBoxRef = useRef(null);
  const addingRef = useRef(false); // ✅ synchronous lock — state updates
                                    // are async, so a fast double-click
                                    // (or a slow network response) could
                                    // fire doAdd twice before the button's
                                    // `disabled` prop visually updates.
                                    // This ref blocks that immediately.
  const lastAddRef = useRef(0);    // ✅ cooldown timestamp — most real
                                    // e-commerce sites briefly ignore
                                    // re-clicks right after a successful
                                    // add (not just while the network
                                    // call is in flight), so an eager
                                    // trackpad/click doesn't fire a
                                    // second legitimate add the user
                                    // didn't really intend.

  /* ── fetch product ────────────────────────────────────────── */
  useEffect(() => {
    setLoading(true);
    setProduct(null); setRelated([]);
    setSelectedSize(""); setQty(1); setInCart(false);
    setImgReady(false); setAllImages([]); setThumbsReady({});
    window.scrollTo({ top: 0, behavior: "smooth" });

    axios.get(`${API}/api/products/${id}`, { timeout: 10000 })
      .then(({ data: p }) => {
        setProduct(p);

        /* build image list */
        const imgs = [
          ...(p.images || []).filter(Boolean).map(getImgUrl),
          ...(p.colorVariants || [])
            .filter(v => v.image)
            .map(v => getImgUrl(v.image))
            .filter(u => !(p.images || []).map(getImgUrl).includes(u)),
        ];
        const final = imgs.length ? imgs : ["/images/s1.png"];
        setAllImages(final);

        /* pick first image */
        let first = final[0];
        if (p.colorVariants?.length) {
          setActiveColor(p.colorVariants[0]);
          if (p.colorVariants[0].image) first = getImgUrl(p.colorVariants[0].image);
        }

        /* mobile: 700px, desktop: 900px — browser picks via srcset */
        setActiveImg(first);
        setActiveIdx(0);

        /* preload priority image */
        const hi = new Image();
        hi.onload  = () => setImgReady(true);
        hi.onerror = () => setImgReady(true);
        hi.src     = cloudOpt(first, 900);

        /* background-load thumbs */
        final.forEach((img, i) => {
          const t = new Image();
          t.onload  = () => setThumbsReady(prev => ({ ...prev, [i]: true }));
          t.onerror = () => setThumbsReady(prev => ({ ...prev, [i]: true }));
          t.src     = cloudOpt(img, 160);
        });

        /* related products (non-blocking) */
        axios.get(`${API}/api/products`, { timeout: 10000 })
          .then(({ data: all }) => {
            const arr  = Array.isArray(all) ? all : all?.products || [];
            const same = arr.filter(x =>
              (x._id || x.id) !== id &&
              x.category?.toLowerCase() === p.category?.toLowerCase()
            ).slice(0, 8);
            const rest = arr.filter(x =>
              (x._id || x.id) !== id &&
              !same.find(r => (r._id || r.id) === (x._id || x.id))
            ).slice(0, 8 - same.length);
            setRelated([...same, ...rest]);
          }).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  /* ── ✅ sync "in cart" state with reality ─────────────────────
     Without this, inCart only ever becomes true via a click inside
     this session — so a customer who already has this exact
     product/size/color in their cart from an earlier visit (cart
     persists across page loads) would still see "ADD TO CART"
     instead of "GO TO CART →" until they clicked it again. This
     keeps the button honest any time the product, size, color, or
     cart contents change. */
  useEffect(() => {
    if (!product) return;
    // ✅ FIXED — must use the SAME resolved colour that doAdd() stores on the cart
    // item (variant name, else the product's own colour). Using only the variant
    // name made the keys differ for products without colour variants, so inCart
    // was reset to false right after every add.
    // No size chosen yet on a product that needs one → nothing in the cart can
    // match, so keep showing ADD TO CART (and the "select a size" prompt).
    if ((product.sizes || []).length > 0 && !selectedSize) { setInCart(false); return; }
    const resolvedColor = activeColor?.name || product.color || "";
    const key = `${product._id}_${selectedSize}_${resolvedColor}`;
    const alreadyInCart = (cartItems||[]).some(c =>
      `${c._id||c.id}_${c.selectedSize}_${c.color||""}` === key
    );
    setInCart(alreadyInCart);
  }, [product, selectedSize, activeColor, cartItems]);

  /* ── swipe ────────────────────────────────────────────────── */
  const onTS = (e) => { txRef.current = e.touches[0].clientX; };
  const onTE = (e) => {
    const diff = txRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) < 50) return;
    if (diff > 0 && activeIdx < allImages.length - 1) changeImg(activeIdx + 1);
    if (diff < 0 && activeIdx > 0)                   changeImg(activeIdx - 1);
  };

  const changeImg = (i) => {
    setActiveImg(allImages[i]);
    setActiveIdx(i);
    setImgReady(false);
  };

  /* ── wishlist ─────────────────────────────────────────────── */
  const isWished   = (wishlist || []).some(p => (p._id || p.id) === product?._id);
  const toggleWish = () => {
    if (!setWishlist || !product) return;
    const pid = product._id || product.id;
    setWishlist(isWished
      ? (wishlist || []).filter(p => (p._id || p.id) !== pid)
      : [...(wishlist || []), product]
    );
  };

  /* ── skeleton ─────────────────────────────────────────────── */
  if (loading) return (
    <div className="pd">
      <style>{STYLES}</style>
      <div className="pd-wrap">
        <div className="sk" style={{ height:12, width:220, marginBottom:16 }} />
        <div className="sk" style={{ width:"100%", aspectRatio:"1/1", borderRadius:14, marginBottom:14 }} />
        <div style={{ display:"flex", gap:6, marginBottom:16 }}>
          {[0,1,2].map(i=><div key={i} className="sk" style={{ flex:1, height:5 }}/>)}
        </div>
        <div className="sk" style={{ height:26, width:"68%", marginBottom:8 }} />
        <div className="sk" style={{ height:12, width:"35%", marginBottom:14 }} />
        <div className="sk" style={{ height:30, width:"28%", marginBottom:18 }} />
        <div style={{ display:"flex", gap:7, marginBottom:14 }}>
          {[0,1,2,3].map(i=><div key={i} className="sk" style={{ width:56, height:38 }}/>)}
        </div>
        <div className="sk" style={{ height:50, marginBottom:10 }} />
        <div className="sk" style={{ height:50 }} />
      </div>
    </div>
  );

  if (!product) return (
    <div className="pd">
      <style>{STYLES}</style>
      <div style={{ minHeight:"50vh", display:"flex", flexDirection:"column",
                    alignItems:"center", justifyContent:"center",
                    padding:"40px 20px", textAlign:"center" }}>
        <div style={{ fontSize:56, marginBottom:14 }}>🔍</div>
        <h2 style={{ fontFamily:"Georgia,serif", fontSize:22, color:"#5c2e1f",
                     fontWeight:700, marginBottom:10 }}>Product Not Found</h2>
        <p style={{ color:"#8a4a30", fontSize:14, maxWidth:300, lineHeight:1.6, marginBottom:24 }}>
          This product may have been removed or the link is broken.
        </p>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", justifyContent:"center" }}>
          <button onClick={() => navigate(-1)}
            style={{ background:"transparent", color:"#5c2e1f", border:"1.5px solid #5c2e1f",
                     padding:"11px 22px", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer" }}>
            ← Go Back
          </button>
          <button onClick={() => navigate("/home")}
            style={{ background:"#5c2e1f", color:"#e8d5a3", border:"none",
                     padding:"11px 22px", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer" }}>
            Browse Products
          </button>
        </div>
      </div>
    </div>
  );

  /* ── derived values ───────────────────────────────────────── */
  // ✅ FIXED — stock now checks the SELECTED SIZE's own count first.
  // Previously this only looked at `activeColor.stock` or fell back
  // straight to `product.stock` (the total pooled across every size
  // combined) — so a blouse with 5 in each of XS/M/XL/X (20 total)
  // let the qty stepper go all the way to 20 for a single size that
  // only actually had 5. Priority: size-specific stock → color
  // variant stock → product total (only when no size selected).
  const getSizeStock = (sizeName) => {
    if (!sizeName || !product.sizeStock) return null;
    let map = product.sizeStock;
    if (map instanceof Map) map = Object.fromEntries(map);
    if (map && typeof map === "object" && !Array.isArray(map)) {
      if (map[sizeName] !== undefined) return Number(map[sizeName]) || 0;
    }
    return null;
  };

  const sizeStockValue = getSizeStock(selectedSize);
  const stock    = sizeStockValue !== null
    ? sizeStockValue
    : (activeColor ? activeColor.stock : product.stock);
  const soldOut  = stock === 0 || !!product.soldOut;
  const discount = product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;
  // Label only (BLOUSE SIZE vs SIZE). The new categories are named things like
  // "Round Neck – Front Hook", which don't contain the word "blouse".
  const isBlouse = /blouse|sleeve|neck|dtf print/i.test(`${product.name} ${product.category||""} ${product.fabric||""}`);
  // ✅ A size is required for EVERY product that has sizes — not only ones whose
  // name happens to contain "blouse".
  const sizeReq  = (product.sizes || []).length > 0 && !selectedSize;

  /* ── add to cart ──────────────────────────────────────────── */
  const doAdd = async () => {
    if (sizeReq) {
      sizeBoxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setSizeShake(true);
      setTimeout(() => setSizeShake(false), 1200);
      setTimeout(() => alert("⚠️ Please select a size first"), 450);
      return false;
    }

    // ✅ Synchronous lock — blocks a second call from starting even
    // if it fires in the same tick as the first (before React has
    // re-rendered the button as disabled).
    if (addingRef.current) return false;

    // ✅ Cooldown — ignore a re-click within 600ms of the previous
    // successful add completing, even though the lock has released.
    // This is the same pattern most real storefronts use so an
    // eager double-click doesn't silently add twice.
    if (Date.now() - lastAddRef.current < 600) return false;

    addingRef.current = true;
    setAdding(true);

    try {
      // ✅ FIXED — this `key` used to be built from `activeColor?.name||""`
      // alone, but the item actually gets STORED with
      // `activeColor?.name || product.color || ""` (see below). For any
      // product with no color variants (activeColor is null), the
      // lookup key came out as "...size_" (empty color) while the
      // stored item's real key was "...size_ALL COLOURS" (or whatever
      // product.color is) — they could never match. Every click on
      // Add to Cart silently created a brand-new duplicate line
      // instead of finding and merging into the existing one. This
      // was the actual root cause of the runaway duplicate-row bug.
      const resolvedColor = activeColor?.name || product.color || "";
      const key = `${product._id}_${selectedSize}_${resolvedColor}`;

      // ✅ CRITICAL FIX — this is what let the cart reach 207 units
      // against a 9-unit stock cap. The stock check previously only
      // validated THIS click's `qty` in isolation — it never knew
      // the item might already be in the cart. A customer who'd
      // already added several units, then clicked Add to Cart again,
      // would have each individual click pass validation (since each
      // one looked small on its own) while the TOTAL silently blew
      // past what's actually in stock. We now check the cumulative
      // total — existing cart quantity + this add — against real
      // stock, exactly like a real checkout would.
      const existingQty = (cartItems || []).find(c =>
        `${c._id||c.id}_${c.selectedSize}_${c.color||""}` === key
      )?.quantity || 0;
      const desiredTotal = existingQty + qty;

      let availableStock = stock;
      try {
        const { data } = await axios.post(`${API}/api/products/check-stock`, {
          productId: product._id, size: selectedSize,
          color: resolvedColor, quantity: desiredTotal,
        });
        availableStock = data.available ?? stock;
        if (!data.canAdd) {
          const canStillAdd = Math.max(0, availableStock - existingQty);
          if (availableStock === 0) {
            alert("Sorry, this item is sold out!");
          } else if (canStillAdd <= 0) {
            alert(`You already have the maximum available (${availableStock}) of this item in your cart.`);
          } else {
            alert(`Only ${availableStock} total available — you can add ${canStillAdd} more.`);
          }
          return false;
        }
      } catch {}

      let toastItem = null;

      // ✅ Functional update — reads the LATEST cart state, not a
      // stale snapshot from when this render happened, so rapid
      // clicks can never each think "not in cart yet" and each
      // append a separate duplicate line.
      setCartItems(prev => {
        const list = prev || [];
        const existingIdx = list.findIndex(c =>
          `${c._id||c.id}_${c.selectedSize}_${c.color||""}` === key
        );

        // ✅ Hard backstop — even if the server check above somehow
        // passed a stale number, the merged quantity can never
        // exceed known available stock.
        const cap = availableStock || 9999;

        if (existingIdx !== -1) {
          const merged = Math.min((list[existingIdx].quantity || 1) + qty, cap);
          const updated = [...list];
          updated[existingIdx] = { ...updated[existingIdx], quantity: merged };
          toastItem = updated[existingIdx];
          return updated;
        }

        const item = {
          ...product, selectedSize,
          color:    resolvedColor,
          image:    cloudOpt(activeImg, 400),
          quantity: Math.min(qty, cap), stock: availableStock,
        };
        toastItem = item;
        return [...list, item];
      });

      if (toastItem) window.showCartToast?.(toastItem);
      setInCart(true);
      lastAddRef.current = Date.now(); // start the cooldown window
      return true;
    } finally {
      // ✅ Always releases the lock, whether we succeeded, hit the
      // stock-check failure, or threw — never leaves the button
      // permanently stuck disabled.
      addingRef.current = false;
      setAdding(false);
    }
  };

  const doBuy = async () => {
    if (inCart) { navigate("/checkout"); return; } // already added — don't add it again
    if (await doAdd()) navigate("/checkout");
  };

  const mainSrc = cloudOpt(activeImg, 900);

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <div className="pd">
      <style>{STYLES}</style>

      <div className="pd-wrap">

        {/* breadcrumb */}
        <nav className="pd-bc">
          <a onClick={() => navigate("/home")}>Home</a>
          <span>›</span>
          <a onClick={() => navigate(-1)}>{product.category || "Products"}</a>
          <span>›</span>
          <span style={{ color:"#5c2e1f", fontWeight:600,
            maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {product.name}
          </span>
        </nav>

        <div className="pd-layout">

          {/* ══ IMAGE COLUMN ══ */}
          <div className="pd-img-col">

            {/* DESKTOP: thumb column + tall image */}
            <div className="pd-desk-gallery" style={{ display:"none" }}>
              {allImages.length > 1 && (
                <div className="pd-thumb-col">
                  {allImages.map((img, i) => (
                    <div key={i}
                      className={`pd-thumb${activeIdx===i?" on":""}`}
                      onClick={() => changeImg(i)}>
                      {!thumbsReady[i] && (
                        <div className="sk" style={{ position:"absolute", inset:0, borderRadius:6 }} />
                      )}
                      <img
                        src={cloudOpt(img, 160)} alt=""
                        loading="lazy"
                        onLoad={()=>setThumbsReady(p=>({...p,[i]:true}))}
                        onError={e=>{e.target.src="/images/s1.png";}}
                        style={{ opacity:thumbsReady[i]?1:0, transition:"opacity .2s" }}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="pd-main-box" style={{ flex:1 }}>
                {!imgReady && <div className="sk" style={{ position:"absolute", inset:0, zIndex:2 }} />}
                {soldOut  && <div className="pd-badge sold">SOLD OUT</div>}
                {discount && !soldOut && <div className="pd-badge disc">{discount}% OFF</div>}
                <div className="pd-wish" onClick={toggleWish}
                  style={{ color:isWished?"#c62828":"#ccc" }}>♥</div>
                <button type="button" className="pd-zoom-btn" aria-label="View full screen"
                  onClick={() => setLightbox(true)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                  </svg>
                </button>
                <img
                  key={mainSrc}
                  src={mainSrc}
                  alt={product.name}
                  className={`pd-main-img pd-zoomable${imgReady?" vis":""}`}
                  onClick={() => setLightbox(true)}
                  onLoad={()=>setImgReady(true)}
                  onError={e=>{e.target.src="/images/s1.png";setImgReady(true);}}
                  style={{ opacity:imgReady?1:0 }}
                />
              </div>
            </div>

            {/* MOBILE: square image + dots + thumb strip */}
            <div className="pd-mob-gallery">
              <div className="pd-main-box"
                onTouchStart={onTS} onTouchEnd={onTE}>
                {!imgReady && <div className="sk" style={{ position:"absolute", inset:0, zIndex:2 }} />}
                {soldOut  && <div className="pd-badge sold">SOLD OUT</div>}
                {discount && !soldOut && <div className="pd-badge disc">{discount}% OFF</div>}
                <div className="pd-wish" onClick={toggleWish}
                  style={{ color:isWished?"#c62828":"#ccc" }}>♥</div>
                <button type="button" className="pd-zoom-btn" aria-label="View full screen"
                  onClick={() => setLightbox(true)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                  </svg>
                </button>
                <img
                  key={mainSrc}
                  src={cloudOpt(activeImg, 700)}
                  alt={product.name}
                  className={`pd-main-img pd-zoomable${imgReady?" vis":""}`}
                  onClick={() => setLightbox(true)}
                  onLoad={()=>setImgReady(true)}
                  onError={e=>{e.target.src="/images/s1.png";setImgReady(true);}}
                  style={{ opacity:imgReady?1:0 }}
                />
                {allImages.length > 1 && (
                  <div className="pd-counter">{activeIdx+1} / {allImages.length}</div>
                )}
              </div>

              {allImages.length > 1 && (
                <div className="pd-dots">
                  {allImages.map((_,i) => (
                    <button key={i} className={`pd-dot${activeIdx===i?" on":""}`}
                      onClick={() => changeImg(i)} />
                  ))}
                </div>
              )}

              {allImages.length > 1 && (
                <div className="pd-thumbs">
                  {allImages.map((img,i) => (
                    <div key={i}
                      className={`pd-thumb${activeIdx===i?" on":""}`}
                      onClick={() => changeImg(i)}
                      style={{ position:"relative" }}>
                      {!thumbsReady[i] && (
                        <div className="sk" style={{ position:"absolute", inset:0 }} />
                      )}
                      <img
                        src={cloudOpt(img,160)} alt=""
                        loading="lazy"
                        onLoad={()=>setThumbsReady(p=>({...p,[i]:true}))}
                        onError={e=>{e.target.src="/images/s1.png";}}
                        style={{ opacity:thumbsReady[i]?1:0, transition:"opacity .2s" }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ══ INFO COLUMN ══ */}
          <div className="pd-info-col">

            <h1 className="pd-name">{product.name}</h1>

            {(product.fabric || product.category) && (
              <p className="pd-meta">
                {product.category}{product.fabric ? ` · ${product.fabric}` : ""}
              </p>
            )}

            <div className="pd-price-row">
              <span className="pd-price">₹{Number(product.price).toLocaleString("en-IN")}</span>
              {product.originalPrice > product.price && (
                <span className="pd-orig">₹{Number(product.originalPrice).toLocaleString("en-IN")}</span>
              )}
              {discount && <span className="pd-off">{discount}% OFF</span>}
            </div>

            {/* ✅ availability status */}
            <div className={`pd-avail ${soldOut ? "out" : "in"}`}>
              <span className="pd-avail-dot" />
              {soldOut
                ? "Out of Stock"
                : SHOW_LOW_STOCK && stock > 0 && stock <= 3
                  ? `Only ${stock} left`
                  : "In Stock"}
            </div>

            <div className="pd-divider" />

            {/* Colour variants */}
            {(product.colorVariants||[]).length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div className="pd-sel-label">
                  COLOUR
                  {activeColor && <em>— {activeColor.name}</em>}
                </div>
                <div className="chip-row">
                  {product.colorVariants.map((v,i) => (
                    <button key={i}
                      className={`chip${activeColor?.name===v.name?" on":""}`}
                      disabled={v.stock===0}
                      onClick={() => {
                        if (v.stock===0) return;
                        setActiveColor(v);
                        if (v.image) {
                          const url = getImgUrl(v.image);
                          const idx = allImages.indexOf(url);
                          changeImg(idx !== -1 ? idx : 0);
                        }
                      }}>
                      {v.name}
                      {v.stock===0 && (
                        <span style={{ fontSize:9, color:"#c62828", display:"block", lineHeight:1.2 }}>sold out</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sizes */}
            {(product.sizes||[]).length > 0 && (
              <div ref={sizeBoxRef} className={sizeShake ? "size-shake" : ""} style={{ marginBottom: sizeReq ? 0 : 16 }}>
                <div className="pd-sel-label">
                  {isBlouse ? "BLOUSE SIZE" : "SIZE"}
                  {selectedSize
                    ? <em>— {selectedSize}</em>
                    : <span className="req-tag">★ Required</span>}
                </div>
                <div className="chip-row">
                  {product.sizes.map((s,i) => {
                    const thisSizeStock = getSizeStock(s);
                    const thisSizeSoldOut = thisSizeStock === 0;
                    return (
                      <button key={i}
                        className={`chip${selectedSize===s?" on":""}${sizeReq&&selectedSize!==s?" err":""}`}
                        disabled={thisSizeSoldOut}
                        title={thisSizeSoldOut ? "Sold out in this size" : undefined}
                        onClick={() => {
                          if (thisSizeSoldOut) return;
                          setSelectedSize(s);
                          setQty(1); // ✅ reset qty so a higher qty picked
                                     // before choosing a size (or from a
                                     // previous larger-stock size) can't
                                     // silently exceed this size's stock
                          setInCart(false); // ✅ switching size means
                                     // this is a fresh add — without
                                     // this the button would wrongly
                                     // stay on "GO TO CART →" for a
                                     // size that was never actually
                                     // added yet
                        }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
                {/* ✅ "CLEAR" link — matches reference site's pattern
                    of letting the customer deselect an option they've
                    already picked, rather than only being able to
                    switch between sizes. */}
                {selectedSize && (
                  <span className="clear-link"
                    onClick={() => { setSelectedSize(""); setQty(1); setInCart(false); }}>
                    CLEAR
                  </span>
                )}
                {sizeReq && (
                  <div className="size-alert">
                    ⚠️ Select a size above — required before adding to cart
                  </div>
                )}
              </div>
            )}

            {/* ✅ Low-stock warning removed per request — the CSS class
                (.low-stock) is left in place, unused, in case you want
                it back later. */}

            {/* ── ACTION ROW — ✅ restored: Add to Cart + Buy Now,
                 same handlers/behavior as before removal. ── */}
            <div className="pd-actions">
              <div className="qty-ctrl">
                <button className="qty-b"
                  onClick={() => setQty(q => Math.max(1, q-1))}
                  disabled={soldOut || qty<=1}>−</button>
                <div className="qty-n">{qty}</div>
                <button className="qty-b"
                  onClick={() => setQty(q => Math.min(stock||10, q+1))}
                  disabled={soldOut || qty>=(stock||10)}>+</button>
              </div>

              {/* ✅ Flipkart-style: once the item is in the cart, the Add to
                  Cart button itself turns into "GO TO CART" (outlined) and
                  Buy Now stays beside it. Changing size/colour switches it
                  back to ADD TO CART for the new variant. */}
              {inCart ? (
                <button className="cta cta-go" onClick={() => navigate("/cart")}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true">
                    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/>
                  </svg>
                  GO TO CART
                </button>
              ) : (
                <button
                  className={`cta cta-cart${soldOut?" cta-sold":""}`}
                  onClick={doAdd}
                  disabled={soldOut || adding}>
                  {soldOut ? "SOLD OUT" : adding ? "ADDING…" : "ADD TO CART"}
                </button>
              )}

              <button
                className={`cta cta-buy${soldOut?" cta-sold":""}`}
                onClick={doBuy}
                disabled={soldOut || adding}>
                {soldOut ? "SOLD OUT" : "BUY NOW →"}
              </button>
            </div>

            {/* Delivery info */}
            <div className="del-badges">
              {/* <div className="del-badge"><span>🚚</span><span>Free shipping all over India</span></div> */}
              <div className="del-badge"><span></span><span>Estimated delivery: 7–10 business days</span></div>
              {/* <div className="del-badge"><span>↩</span><span>Easy 7-day returns</span></div> */}
              <div className="del-badge"><span></span><span>100% authentic handloom product</span></div>
            </div>

            {/* Description */}
            {product.description && (
              <div style={{ borderTop:"1px solid #ecd9bd", paddingTop:14 }}>
                <div className="pd-desc-label">PRODUCT DETAILS</div>
                <p className="pd-desc">{product.description}</p>
              </div>
            )}

            {/* Notify if sold out */}
            {soldOut && (
              <div className="notify-box">
                <p style={{ color:"#5c2e1f", fontSize:14, fontWeight:600, margin:"0 0 12px" }}>
                   Get notified when back in stock
                </p>
                <a className="wa-link"
                  href={`https://wa.me/+91 9390905464?text=${encodeURIComponent(
                    `Hi! Please notify me when "${product.name}" is back in stock.`)}`}
                  target="_blank" rel="noreferrer">
                  <WaIcon /> Notify on WhatsApp
                </a>
              </div>
            )}
          </div>
        </div>

        {/* ══ RELATED PRODUCTS ══ */}
        {related.length > 0 && (
          <div className="rel-section">
            <div className="rel-header">
              <div className="rel-rule">
                <div className="rel-line" />
                <h2 className="rel-title">Related Products</h2>
                <div className="rel-line" />
              </div>
              <div className="rel-sub">MORE FROM {(product.category||"STORE").toUpperCase()}</div>
            </div>
            <div className="rel-grid">
              {related.map(p => {
                const pid   = p._id || p.id;
                const img   = cloudOpt(getImgUrl(p.images?.[0] || p.image), 480);
                const pWish = (wishlist||[]).some(w => (w._id||w.id) === pid);
                const pSold = p.soldOut || Number(p.stock) === 0;
                const pDisc = p.originalPrice > p.price
                  ? Math.round(((p.originalPrice-p.price)/p.originalPrice)*100) : null;
                return (
                  <div key={pid} className="rel-card"
                    onClick={() => navigate(`/product/${pid}`)}>
                    <div className="rel-img-wrap">
                      <img className="rel-img" src={img} alt={p.name}
                        loading="lazy"
                        onError={e=>{e.target.src="/images/s1.png";}}
                        style={{ filter:pSold?"grayscale(25%)":"none" }} />
                      {pSold && <div className="rel-sold-banner">SOLD OUT</div>}
                      {pDisc && !pSold && <div className="rel-disc-tag">{pDisc}% OFF</div>}
                      <button className="rel-wish"
                        onClick={e=>{
                          e.stopPropagation();
                          if (!setWishlist) return;
                          setWishlist(pWish
                            ? (wishlist||[]).filter(w=>(w._id||w.id)!==pid)
                            : [...(wishlist||[]),p]);
                        }}>
                        <span style={{ color:pWish?"#c62828":"#bbb" }}>{pWish?"♥":"♡"}</span>
                      </button>
                    </div>
                    <div className="rel-body">
                      <p className="rel-pname">{p.name}</p>
                      <div className="rel-pmeta">{p.fabric}{p.category?` · ${p.category}`:""}</div>
                      <div>
                        <span className="rel-pprice">₹{Number(p.price).toLocaleString("en-IN")}</span>
                        {p.originalPrice>p.price && (
                          <span className="rel-porig">₹{Number(p.originalPrice).toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    </div>
                    <button className="rel-cta"
                      style={{ background:pSold?"#888":"#4a2419", color:pSold?"#fff":"#e8d5a3" }}
                      onClick={e=>{e.stopPropagation();navigate(`/product/${pid}`);}}>
                      {pSold?"Sold Out — View →":"View Product →"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>

      {lightbox && allImages.length > 0 && (
        <Lightbox
          images={allImages}
          index={activeIdx}
          name={product.name}
          onIndex={changeImg}
          onClose={() => setLightbox(false)}
        />
      )}
    </div>
  );
}